import React, { useState, useEffect } from "react";
import { Box, Divider, TextField, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Paper, Alert, Link } from "@mui/material";
import api from "../Api";
import RefreshIcon from "@mui/icons-material/Refresh";
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { addDaysToDateString } from "../utils/date";
import { buildGroupSummary } from "../utils/scanGroups";


const ScanSummary = ({ selectedDate, setSelectedDate, readOnlyDate = false, accounts = [], onOpenNotScanned }) => {
  const [scanSummary, setScanSummary] = useState([]);
  const [statusMessage, setStatusMessage] = useState(null);

  const handleDateChange = (days) => {
    setSelectedDate(addDaysToDateString(selectedDate, days));
  };

  // Bumped by the refresh button. The panel otherwise only reloads when the date
  // changes, so on a page left open the numbers quietly go stale while scanning
  // continues.
  const [reloadNonce, setReloadNonce] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    const fetchScanSummary = async () => {
      try {
        const response = await api.get("/api/scan-summary", { params: { date: selectedDate } });
        if (Array.isArray(response.data)) {
          setScanSummary(response.data);
          setStatusMessage(null);
        } else {
          setStatusMessage({ type: "error", message: "Unexpected response format." });
        }
      } catch (error) {
        console.error("❌ Error fetching scan summary:", error);
        setStatusMessage({ type: "error", message: "Failed to fetch scan summary." });
      }
    };
    fetchScanSummary().finally(() => setRefreshing(false));
  }, [selectedDate, reloadNonce]);

  const totalOrders = scanSummary.reduce((sum, row) => sum + Number(row.total_orders ?? row.total_quantity ?? 0), 0);
  const totalScanned = scanSummary.reduce((sum, row) => sum + Number(row.scanned_entries ?? row.scanned_quantity ?? 0), 0);
  const totalRemaining = totalOrders - totalScanned;
  const groupSummary = buildGroupSummary(scanSummary);

  const sortedScanSummary = accounts.length
    ? [...scanSummary]
        .filter(account => Number(account.total_orders ?? account.total_quantity ?? 0) > 0)
        .sort((a, b) => {
          const indexA = accounts.findIndex(acc => acc.name === a.account_name);
          const indexB = accounts.findIndex(acc => acc.name === b.account_name);
          return indexA - indexB;
        })
    : scanSummary.filter(account => Number(account.total_orders ?? account.total_quantity ?? 0) > 0);

  return (
    <Box sx={{ px: 2, py: 3, display: "flex", flexDirection: "column", alignItems: "center", width: { xs: "90%", md: "60%" }, mx: "auto" }}>
      {statusMessage?.message && (
        <Alert severity={statusMessage.type} sx={{ mb: 2, width: "100%" }}>{statusMessage.message}</Alert>
      )}

      {/* ✅ Top Charcoal Bar */}
      <Box
        sx={{
          width: "100%",
          backgroundColor: "#333",
          color: "white",
          borderRadius: 3,
          border: "1px solid #555",
          px: 2, py: 2,
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: "bold",
            fontFamily: `'Caveat','Patrick Hand','Comic Sans MS'`,
            fontStyle: "normal",
            whiteSpace: "nowrap"
          }}
        >
          📊 Scan Progress
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            onClick={() => { setRefreshing(true); setReloadNonce((n) => n + 1); }}
            disabled={refreshing}
            title="Refresh scan progress"
            sx={{ color: "white",
                  "& svg": refreshing
                    ? { animation: "spin 0.8s linear infinite" } : {},
                  "@keyframes spin": { from: { transform: "rotate(0deg)" },
                                       to: { transform: "rotate(360deg)" } } }}
          >
            <RefreshIcon />
          </IconButton>
          <IconButton onClick={() => handleDateChange(-1)} sx={{ color: "white" }}>
            <ChevronLeftIcon />
          </IconButton>
          <TextField
            type="date"
            size="small"
            value={selectedDate}
            onChange={(e) => !readOnlyDate && setSelectedDate(e.target.value)}
            InputProps={{ readOnly: readOnlyDate }}
            sx={{
              width: 140,
              input: { color: "white" },
              label: { color: "white" },
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderColor: "white" },
                "&:hover fieldset": { borderColor: "#bbb" },
                "&.Mui-focused fieldset": { borderColor: "#bbb" },
              },
            }}
          />
          <IconButton onClick={() => handleDateChange(1)} sx={{ color: "white" }}>
            <ChevronRightIcon />
          </IconButton>
        </Box>
      </Box>

      {/* ✅ Rest of the Box remains the same */}
      <Box sx={{ width: "100%", p: 2, bgcolor: "#f8f8f8", borderRadius: 2 }}>
        {scanSummary.length > 0 ? (
          <>
            {sortedScanSummary.map((account, index) => {
              const totalOrdersForAccount = Number(account.total_orders ?? account.total_quantity ?? 0);
              const scannedOrdersForAccount = Number(account.scanned_entries ?? account.scanned_quantity ?? 0);
              const progress = totalOrdersForAccount > 0
                ? ((scannedOrdersForAccount / totalOrdersForAccount) * 100).toFixed(2)
                : 0;

              return (
                <Box key={index} sx={{ marginBottom: "15px" }}>
                  <Typography
                    variant="body1"
                    sx={{
                      marginBottom: "5px",
                      fontWeight: "bold",
                      fontFamily: "inherit",
                    }}
                  >
                    {account.account_name}: {scannedOrdersForAccount}/{totalOrdersForAccount} ({progress}%)
                  </Typography>

                  <Box sx={{ position: "relative", width: "100%", height: "12px", borderRadius: "5px", backgroundColor: "#e0e0e0" }}>
                    <Box
                      sx={{
                        width: `${progress}%`,
                        height: "100%",
                        backgroundColor: progress === "100" ? "#4caf50" : "#1976d2",
                        borderRadius: "5px",
                        transition: "width 0.5s ease-in-out",
                      }}
                    />
                  </Box>
                </Box>
              );
            })}

            <Divider sx={{ margin: "20px 0" }} />

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 2 }}>
              {/* Overall totals */}
              <Box sx={{ flex: 1, minWidth: 200 }}>
                <Typography variant="body1" sx={{ fontWeight: "bold", fontFamily: "inherit" }}>
                  Total (All Accounts): {totalOrders > 0 ? ((totalScanned / totalOrders) * 100).toFixed(2) : "0.00"}%
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: "bold", fontFamily: "inherit" }}>
                  Total Orders: {totalOrders}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: "bold", fontFamily: "inherit" }}>
                  Total Scanned: {totalScanned}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: "bold", fontFamily: "inherit" }}>
                  Total Remaining: {totalRemaining}
                </Typography>
              </Box>

              {/* Group-wise breakdown (right side). Remaining is clickable. */}
              {groupSummary.length > 0 && (
                <Box sx={{ minWidth: 220, bgcolor: "#fff", border: "1px solid #e0e0e0", borderRadius: 2, p: 1.5 }}>
                  {groupSummary.map((g) => (
                    <Box key={g.group} sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 1, py: 0.25 }}>
                      <Typography sx={{ fontWeight: "bold", fontFamily: "inherit" }}>{g.group}</Typography>
                      <Typography variant="body2" component="span">
                        {g.scanned}/{g.total}/
                        {onOpenNotScanned ? (
                          <Link
                            component="button"
                            type="button"
                            underline="hover"
                            onClick={() => onOpenNotScanned(g.group)}
                            sx={{ fontWeight: "bold", color: g.remaining > 0 ? "error.main" : "success.main", verticalAlign: "baseline" }}
                            title={`Show ${g.remaining} not-scanned in ${g.group}`}
                          >
                            -{g.remaining}
                          </Link>
                        ) : (
                          <Typography component="span" sx={{ fontWeight: "bold", color: g.remaining > 0 ? "error.main" : "success.main" }}>
                            -{g.remaining}
                          </Typography>
                        )}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </>
        ) : (
          <Typography variant="body1" sx={{ color: "gray" }}>
            No scan data available.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default ScanSummary;
